import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Megaphone, Search, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { useUserRole } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BackfillRunsPanel } from "@/components/admin/BackfillRunsPanel";
import { LibraryCoverOrphansPanel } from "@/components/admin/LibraryCoverOrphansPanel";
import { NfcBackfillPanel } from "@/components/admin/NfcBackfillPanel";
import { TaxNoticeCard } from "@/components/admin/TaxNoticeCard";
import { UsersTab } from "@/components/admin/UsersTab";
import { EmailDeliverabilityPanel } from "@/components/admin/EmailDeliverabilityPanel";
import { JobsTab } from "@/components/admin/JobsTab";
import { BulkOpsTab } from "@/components/admin/BulkOpsTab";
import { FeedbackTab } from "@/components/admin/FeedbackTab";
import { BetaTab } from "@/components/admin/BetaTab";
import { exportAuditEntriesToCsv, filterAuditEntries, formatAdminAuditEntry } from "@/lib/featureFlagAdmin";
import { AppSeo } from "@/components/AppSeo";

const PAGE_SIZE = 50;

const Admin = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "users";
  const { isAdmin, loading } = useUserRole();
  const [actions, setActions] = useState<Tables<"admin_actions">[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditOffset, setAuditOffset] = useState(0);
  const [auditIdentities, setAuditIdentities] = useState<Record<string, { display_name: string | null; email: string | null }>>({});
  const [auditDistinctActions, setAuditDistinctActions] = useState<string[]>([]);
  const [auditActionFilter, setAuditActionFilter] = useState<string>("all");
  const [auditActorFilter, setAuditActorFilter] = useState<string>("all");
  const [auditTargetFilter, setAuditTargetFilter] = useState<string>("all");
  const [auditFromDate, setAuditFromDate] = useState<string>("");
  const [auditToDate, setAuditToDate] = useState<string>("");
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditQuery, setAuditQuery] = useState("");

  const loadAudit = async (overrides?: { offset?: number; action?: string; admin_user_id?: string; target_user_id?: string; from?: string; to?: string }) => {
    setAuditLoading(true);
    const offset = overrides?.offset ?? auditOffset;
    const actionVal = overrides?.action ?? (auditActionFilter !== "all" ? auditActionFilter : undefined);
    const actorVal = overrides?.admin_user_id ?? (auditActorFilter !== "all" ? auditActorFilter : undefined);
    const targetVal = overrides?.target_user_id ?? (auditTargetFilter !== "all" ? auditTargetFilter : undefined);
    const fromVal = overrides?.from ?? (auditFromDate ? new Date(auditFromDate).toISOString() : undefined);
    const toVal = overrides?.to ?? (auditToDate ? new Date(`${auditToDate}T23:59:59`).toISOString() : undefined);

    const body: Record<string, unknown> = { limit: PAGE_SIZE, offset };
    if (actionVal) body.action = actionVal;
    if (actorVal) body.admin_user_id = actorVal;
    if (targetVal) body.target_user_id = targetVal;
    if (fromVal) body.from = fromVal;
    if (toVal) body.to = toVal;

    const { data, error } = await supabase.functions.invoke("admin-audit-list", { body });
    setAuditLoading(false);
    if (error) { toast.error(error.message); return; }
    setActions((data?.rows ?? []) as Tables<"admin_actions">[]);
    setAuditTotal(data?.total ?? 0);
    setAuditIdentities((prev) => ({ ...prev, ...(data?.identities ?? {}) }));
    if (Array.isArray(data?.distinct_actions)) setAuditDistinctActions(data.distinct_actions);
  };

  useEffect(() => {
    if (!isAdmin) return;
    void loadAudit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const labelForIdentity = (id: string | null | undefined) => {
    if (!id) return null;
    const ident = auditIdentities[id];
    if (!ident) return id.slice(0, 8);
    return ident.display_name || ident.email || id.slice(0, 8);
  };

  const formattedActions = useMemo(
    () => actions.map((action) => ({
      ...action,
      formatted: formatAdminAuditEntry(action),
      actorLabel: labelForIdentity(action.admin_user_id),
      targetLabel: labelForIdentity(action.target_user_id),
    })),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [actions, auditIdentities]);

  const auditActorOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const a of actions) if (a.admin_user_id) ids.add(a.admin_user_id);
    return [...ids].map((id) => ({ id, label: labelForIdentity(id) ?? id }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, auditIdentities]);

  const auditTargetOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const a of actions) if (a.target_user_id) ids.add(a.target_user_id);
    return [...ids].map((id) => ({ id, label: labelForIdentity(id) ?? id }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, auditIdentities]);

  const filteredActions = useMemo(() => filterAuditEntries(formattedActions, auditQuery), [formattedActions, auditQuery]);

  const exportAuditLog = () => {
    const csv = exportAuditEntriesToCsv(filteredActions);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `audit-log-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(filteredActions.length === formattedActions.length ? "Audit log exported" : "Filtered audit results exported");
  };

  return (
    <>
    <AppSeo title="Admin" />
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-serif text-3xl font-semibold">Admin</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => navigate("/admin/changelog")} variant="outline">
            <Sparkles className="h-4 w-4" /> Changelog
          </Button>
          <Button onClick={() => navigate("/admin/announcements")} variant="outline">
            <Megaphone className="h-4 w-4" /> Announcements
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setSearchParams((sp) => { sp.set("tab", v); return sp; })}>
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <TabsList>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="invites">Invites</TabsTrigger>
            <TabsTrigger value="ops">Ops</TabsTrigger>
            <TabsTrigger value="jobs">Jobs</TabsTrigger>
            <TabsTrigger value="bulk">Bulk Ops</TabsTrigger>
            <TabsTrigger value="feedback">Feedback</TabsTrigger>
            <TabsTrigger value="beta">Beta</TabsTrigger>
            <TabsTrigger value="audit">Audit Log</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="users" className="space-y-4">
          <UsersTab />
        </TabsContent>

        <TabsContent value="invites">
          <Card className="shadow-soft">
            <CardContent className="py-8 text-sm text-muted-foreground">
              Invites are sent automatically when a comp account is created on the Super Admin → Comp Accounts tab.
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ops" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => navigate("/admin/emails/preview")} variant="outline" size="sm">
              Preview email templates
            </Button>
          </div>
          <EmailDeliverabilityPanel />
          <BackfillRunsPanel />
          <LibraryCoverOrphansPanel />
          <NfcBackfillPanel />
          <TaxNoticeCard />
        </TabsContent>

        <TabsContent value="jobs" className="space-y-4"><JobsTab /></TabsContent>
        <TabsContent value="bulk" className="space-y-4"><BulkOpsTab /></TabsContent>
        <TabsContent value="feedback" className="space-y-4"><FeedbackTab /></TabsContent>
        <TabsContent value="beta" className="space-y-4"><BetaTab /></TabsContent>

        <TabsContent value="audit">
          <Card className="shadow-soft">
            <CardHeader className="gap-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <CardTitle>Audit Log</CardTitle>
                  <p className="mt-1 text-sm text-muted-foreground">Filter recent admin activity by action, actor, target, or date range. Results are paginated server-side.</p>
                </div>
                <Button variant="outline" onClick={exportAuditLog} disabled={filteredActions.length === 0}>
                  <Download className="h-4 w-4" /> Export CSV
                </Button>
              </div>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
                <Select value={auditActionFilter} onValueChange={(v) => { setAuditActionFilter(v); setAuditOffset(0); void loadAudit({ offset: 0, action: v === "all" ? undefined : v }); }}>
                  <SelectTrigger aria-label="Filter by action"><SelectValue placeholder="All actions" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All actions</SelectItem>
                    {auditDistinctActions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={auditActorFilter} onValueChange={(v) => { setAuditActorFilter(v); setAuditOffset(0); void loadAudit({ offset: 0, admin_user_id: v === "all" ? undefined : v }); }}>
                  <SelectTrigger aria-label="Filter by actor"><SelectValue placeholder="Any admin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any admin</SelectItem>
                    {auditActorOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={auditTargetFilter} onValueChange={(v) => { setAuditTargetFilter(v); setAuditOffset(0); void loadAudit({ offset: 0, target_user_id: v === "all" ? undefined : v }); }}>
                  <SelectTrigger aria-label="Filter by target"><SelectValue placeholder="Any target" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any target</SelectItem>
                    {auditTargetOptions.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" value={auditFromDate} onChange={(e) => { setAuditFromDate(e.target.value); setAuditOffset(0); void loadAudit({ offset: 0, from: e.target.value ? new Date(e.target.value).toISOString() : undefined }); }} aria-label="From date" />
                <Input type="date" value={auditToDate} onChange={(e) => { setAuditToDate(e.target.value); setAuditOffset(0); void loadAudit({ offset: 0, to: e.target.value ? new Date(`${e.target.value}T23:59:59`).toISOString() : undefined }); }} aria-label="To date" />
              </div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={auditQuery} onChange={(e) => setAuditQuery(e.target.value)} placeholder="Search within the current page" className="pl-9" aria-label="Search Audit Log" />
              </div>
            </CardHeader>
            <CardContent className="space-y-3 py-4 text-sm">
              {auditLoading && <p className="text-muted-foreground">Loading…</p>}
              {!auditLoading && actions.length === 0 && <p className="text-muted-foreground">No actions match those filters.</p>}
              {!auditLoading && actions.length > 0 && filteredActions.length === 0 && <p className="text-muted-foreground">No audit entries match that search.</p>}
              {filteredActions.map((action) => (
                <div key={action.id} className="rounded-md border border-border bg-background px-4 py-3">
                  <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        <p className="font-medium">{action.formatted.title}</p>
                      </div>
                      {action.formatted.detail && <p className="text-sm text-muted-foreground">{action.formatted.detail}</p>}
                      <p className="text-xs text-muted-foreground">
                        by {action.actorLabel ?? "—"}{action.targetLabel ? <> → {action.targetLabel}</> : null}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs text-muted-foreground">{new Date(action.created_at).toLocaleString()}</span>
                  </div>
                  {action.formatted.fields.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {action.formatted.fields.map((item) => (
                        <div key={`${action.id}-${item.label}`} className="rounded-md border border-border bg-muted/30 px-2 py-1 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{item.label}:</span> {item.value}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {auditTotal > 0 && (
                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-muted-foreground">
                    Showing {Math.min(auditOffset + 1, auditTotal)}–{Math.min(auditOffset + actions.length, auditTotal)} of {auditTotal}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={auditOffset === 0 || auditLoading} onClick={() => { const next = Math.max(auditOffset - PAGE_SIZE, 0); setAuditOffset(next); void loadAudit({ offset: next }); }}>Prev</Button>
                    <Button variant="outline" size="sm" disabled={auditOffset + actions.length >= auditTotal || auditLoading} onClick={() => { const next = auditOffset + PAGE_SIZE; setAuditOffset(next); void loadAudit({ offset: next }); }}>Next</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
    </>
  );
};

export default Admin;
